import React, { useEffect, useState, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { useI18n } from '../i18n/i18nContext';
import { useUser } from '../contexts/UserContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useBookingCartStore } from '../stores/bookingCartStore';
import { useServiceStore } from '../stores/serviceStore';
import { usePromotionsStore } from '../stores/promotionsStore';
import LanguageSwitcher from './LanguageSwitcher';
import LocalizedLink from './LocalizedLink';
import Footer from './Footer';
import SEO from './SEO';
import ImpersonationBanner from './ImpersonationBanner';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { isAuthenticated, loginWithRedirect, user: auth0User } = useAuth0();
  const { user: dbUser } = useUser();
  const { isImpersonating, impersonatedUser } = useImpersonation();
  const location = useLocation();
  const { t, language } = useI18n();
  const items = useBookingCartStore((state) => state.items);
  const { services, fetchServices } = useServiceStore();
  const { checkActivePromotions } = usePromotionsStore();
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [isPricingDropdownOpen, setIsPricingDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (services.length === 0) {
      fetchServices();
    }
    checkActivePromotions();
  }, [fetchServices, services.length, checkActivePromotions]);

  useEffect(() => {
    const langCode =
      language === 'et' ? 'et' : language === 'ru' ? 'ru' : language === 'fi' ? 'fi' : 'en';
    document.documentElement.lang = langCode;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const existingScript = document.querySelector('script[src*="cookie-script.com"]');
    if (existingScript) {
      const scriptSrc = existingScript.getAttribute('src');
      const win = window as any;
      if (win.CookieScript && win.CookieScript.instance) {
        win.CookieScript.instance.hide();
      }
      delete win.CookieScript;
      existingScript.remove();

      const cookieScriptElements = document.querySelectorAll('[id^="cookiescript_"]');
      cookieScriptElements.forEach((el) => el.remove());

      const newScript = document.createElement('script');
      newScript.src = scriptSrc || '';
      newScript.async = true;
      newScript.charset = 'UTF-8';
      newScript.setAttribute('data-cs-lang', langCode);
      newScript.id = 'cookiescript_script';
      document.head.appendChild(newScript);
    }
  }, [language]);

  // Helper function to get localized service name
  const getServiceName = (service: any): string => {
    const langKey = `name_${language}` as keyof typeof service;
    return service[langKey] || service.name;
  };

  // Check if we're on the home page (packages page)
  const isHomePage = !!location.pathname.match(/^\/(en|ee|ru|fi)?\/?$/);
  const navClasses = isHomePage
    ? 'absolute top-0 left-0 right-0 z-20 bg-transparent text-white'
    : 'bg-white shadow-sm border-b';

  return (
    <div className="flex min-h-screen flex-col overflow-x-hidden bg-gray-50">
      {/* Default SEO - pages can override by rendering their own SEO component */}
      <SEO />
      {/* Impersonation banner - shows when admin is viewing as a customer */}
      <ImpersonationBanner />
      <nav className={navClasses}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 justify-between">
            <div className="flex items-center">
              <LocalizedLink
                to="/"
                className={`flex items-center space-x-2 ${
                  isHomePage ? 'mt-2 rounded-3xl bg-white bg-opacity-5 p-4 backdrop-blur-sm' : ''
                }`}
              >
                <img
                  src={isHomePage ? '/images/logo-white.svg' : '/images/logo.svg'}
                  alt={t.nav.title}
                  className="h-10 w-auto object-contain md:h-10"
                />
              </LocalizedLink>
              <div className="hidden items-baseline space-x-0.5 md:ml-6 md:flex lg:space-x-1 xl:space-x-2">
                <div
                  className="relative"
                  onMouseEnter={() => setIsServicesDropdownOpen(true)}
                  onMouseLeave={() => setIsServicesDropdownOpen(false)}
                >
                  <LocalizedLink
                    to="/#services"
                    className={`rounded-md px-1.5 py-2 text-sm font-medium lg:px-2 xl:px-3 ${isHomePage ? 'text-white hover:bg-white hover:bg-opacity-10 hover:text-gray-200' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'}`}
                  >
                    {t.nav.services}
                  </LocalizedLink>
                  {isServicesDropdownOpen && services.length > 0 && (
                    <ul
                      className="absolute left-0 top-full z-50 min-w-[240px] rounded-lg border-0 bg-white/95 py-3 shadow-2xl backdrop-blur-sm"
                      style={{ backdropFilter: 'blur(4px)' }}
                    >
                      {services.map((service) => (
                        <li key={service.id}>
                          <LocalizedLink
                            to={`/services/${service.slug}`}
                            className="block px-5 py-3 text-sm text-gray-800 transition-colors duration-150 hover:bg-blue-50"
                            onClick={() => setIsServicesDropdownOpen(false)}
                          >
                            {getServiceName(service)}
                          </LocalizedLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <LocalizedLink
                  to="/p/science-studies"
                  className={`rounded-md px-1.5 py-2 text-center text-sm font-medium lg:px-2 xl:px-3 ${isHomePage ? 'text-white hover:bg-white hover:bg-opacity-10 hover:text-gray-200' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'}`}
                >
                  {t.nav.scienceStudies}
                </LocalizedLink>
                <LocalizedLink
                  to="/gallery"
                  className={`rounded-md px-1.5 py-2 text-center text-sm font-medium lg:px-2 xl:px-3 ${isHomePage ? 'text-white hover:bg-white hover:bg-opacity-10 hover:text-gray-200' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'}`}
                >
                  {t.nav.gallery}
                </LocalizedLink>
                <LocalizedLink
                  to="/contact"
                  className={`rounded-md px-1.5 py-2 text-center text-sm font-medium lg:px-2 xl:px-3 ${isHomePage ? 'text-white hover:bg-white hover:bg-opacity-10 hover:text-gray-200' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'}`}
                >
                  {t.nav.about}
                </LocalizedLink>
                <div
                  className="relative"
                  onMouseEnter={() => setIsPricingDropdownOpen(true)}
                  onMouseLeave={() => setIsPricingDropdownOpen(false)}
                >
                  <LocalizedLink
                    to="/pricing"
                    className={`rounded-md px-1.5 py-2 text-sm font-medium lg:px-2 xl:px-3 ${isHomePage ? 'text-white hover:bg-white hover:bg-opacity-10 hover:text-gray-200' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'}`}
                  >
                    {t.nav.pricing}
                  </LocalizedLink>
                  {isPricingDropdownOpen && (
                    <ul
                      className="absolute left-0 top-full z-50 min-w-[240px] rounded-lg border-0 bg-white/95 py-3 shadow-2xl backdrop-blur-sm"
                      style={{ backdropFilter: 'blur(4px)' }}
                    >
                      <li>
                        <LocalizedLink
                          to="/pricing"
                          className="block px-5 py-3 text-sm text-gray-800 transition-colors duration-150 hover:bg-blue-50"
                          onClick={() => setIsPricingDropdownOpen(false)}
                        >
                          {t.nav.memberships}
                        </LocalizedLink>
                      </li>
                      <li>
                        <LocalizedLink
                          to="/pricing#packages"
                          className="block px-5 py-3 text-sm text-gray-800 transition-colors duration-150 hover:bg-blue-50"
                          onClick={() => setIsPricingDropdownOpen(false)}
                        >
                          {t.nav.packagesShort}
                        </LocalizedLink>
                      </li>
                      <li>
                        <LocalizedLink
                          to="/promotions"
                          className="block px-5 py-3 text-sm text-gray-800 transition-colors duration-150 hover:bg-blue-50"
                          onClick={() => setIsPricingDropdownOpen(false)}
                        >
                          {t.nav.promotions}
                        </LocalizedLink>
                      </li>
                      <li>
                        <LocalizedLink
                          to="/gift-card"
                          className="block px-5 py-3 text-sm text-gray-800 transition-colors duration-150 hover:bg-blue-50"
                          onClick={() => setIsPricingDropdownOpen(false)}
                        >
                          {t.nav.giftCards}
                        </LocalizedLink>
                      </li>
                    </ul>
                  )}
                </div>
                <LocalizedLink
                  to="/book"
                  className={`ml-1 hidden items-center rounded-lg px-2 py-1.5 text-center text-sm font-medium md:flex lg:ml-2 lg:px-2.5 lg:py-2 xl:ml-3 xl:px-4 ${isHomePage ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-sky-500 bg-opacity-10 text-black hover:bg-opacity-20'}`}
                >
                  {t.nav.bookSession}
                </LocalizedLink>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {items.length > 0 && (
                <LocalizedLink
                  to="/checkout"
                  className={`relative mr-4 p-2 ${isHomePage ? 'text-white hover:text-gray-200' : 'text-gray-700 hover:text-gray-900'}`}
                  aria-label="View booking cart"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  <span className="absolute right-0 top-0 flex h-5 w-5 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full bg-red-600 text-xs font-bold text-white">
                    {items.length}
                  </span>
                </LocalizedLink>
              )}
              <LanguageSwitcher isHomePage={isHomePage} />
              {!isAuthenticated && !isImpersonating ? (
                <div className="hidden items-center space-x-2 md:flex">
                  <button
                    onClick={() =>
                      loginWithRedirect({
                        appState: {
                          returnTo: window.location.pathname,
                          from: 'header',
                        },
                        authorizationParams: {
                          screen_hint: 'login',
                        },
                      })
                    }
                    className={`rounded-md px-4 py-2 text-sm font-medium ${isHomePage ? 'border border-white border-opacity-50 bg-transparent text-white hover:bg-white hover:bg-opacity-10' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'}`}
                  >
                    {t.nav.login}
                  </button>
                  <button
                    onClick={() =>
                      loginWithRedirect({
                        appState: {
                          returnTo: window.location.pathname,
                          from: 'header',
                        },
                        authorizationParams: {
                          screen_hint: 'signup',
                        },
                      })
                    }
                    className={`rounded-md px-4 py-2 text-sm font-medium ${isHomePage ? 'border border-white bg-white bg-opacity-20 text-white backdrop-blur-sm hover:bg-opacity-30' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                  >
                    {t.nav.signUp}
                  </button>
                </div>
              ) : (
                <div className="hidden items-center space-x-2 md:flex">
                  <LocalizedLink
                    to="/account"
                    className={`flex items-center space-x-2 rounded-md px-2 py-1 ${isHomePage ? 'hover:bg-white hover:bg-opacity-10' : 'hover:bg-gray-100'}`}
                  >
                    {isImpersonating ? (
                      // Show impersonated user avatar placeholder during impersonation
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-500 text-sm font-bold text-white">
                        {(impersonatedUser?.name || impersonatedUser?.email || '?')
                          .charAt(0)
                          .toUpperCase()}
                      </div>
                    ) : auth0User?.picture ? (
                      <img
                        src={auth0User.picture}
                        alt={dbUser?.name || auth0User.name}
                        className="h-8 w-8 rounded-full"
                      />
                    ) : null}
                    <span
                      className={`whitespace-nowrap text-sm font-medium ${isHomePage ? 'text-white' : 'text-gray-700'}`}
                    >
                      {t.nav.myAccount}
                    </span>
                  </LocalizedLink>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="animate-in fade-in slide-in-from-bottom-4 fixed inset-0 z-40 overflow-y-auto bg-white px-6 pb-24 pt-20 duration-200 md:hidden">
          <div className="space-y-8">
            {/* Services Section */}
            <div>
              <h3 className="mb-4 border-b pb-2 text-lg font-bold text-gray-900">
                {t.nav.services}
              </h3>
              <ul className="space-y-3">
                {services.map((service) => (
                  <li key={service.id}>
                    <LocalizedLink
                      to={`/services/${service.slug}`}
                      className="block text-base text-gray-600 hover:text-blue-600"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {getServiceName(service)}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>

            {/* Science Studies */}
            <div>
              <LocalizedLink
                to="/p/science-studies"
                className="mb-4 block pb-2 text-lg font-bold text-gray-900"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t.nav.scienceStudies}
              </LocalizedLink>
            </div>

            {/* Gallery */}
            <div>
              <LocalizedLink
                to="/gallery"
                className="mb-4 block pb-2 text-lg font-bold text-gray-900"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t.nav.gallery}
              </LocalizedLink>
            </div>

            {/* Pricing Section */}
            <div>
              <h3 className="mb-4 border-b pb-2 text-lg font-bold text-gray-900">
                {t.nav.pricing}
              </h3>
              <ul className="space-y-3">
                <li>
                  <LocalizedLink
                    to="/pricing"
                    className="block text-base text-gray-600 hover:text-blue-600"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {t.nav.memberships}
                  </LocalizedLink>
                </li>
                <li>
                  <LocalizedLink
                    to="/pricing#packages"
                    className="block text-base text-gray-600 hover:text-blue-600"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {t.nav.packagesShort}
                  </LocalizedLink>
                </li>
                <li>
                  <LocalizedLink
                    to="/promotions"
                    className="block text-base text-gray-600 hover:text-blue-600"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {t.nav.promotions}
                  </LocalizedLink>
                </li>
                <li>
                  <LocalizedLink
                    to="/gift-card"
                    className="block text-base text-gray-600 hover:text-blue-600"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {t.nav.giftCards}
                  </LocalizedLink>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-gray-200 bg-white md:hidden">
        <div className="flex h-16 items-center justify-around px-2">
          {/* Book Session - Updated to stand out */}
          <LocalizedLink
            to="/book"
            className={`mx-1 flex min-w-0 flex-1 flex-col items-center justify-center rounded-lg px-2 py-1.5 transition-colors ${
              location.pathname.includes('/book')
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-sky-500 bg-opacity-10 text-blue-900 hover:bg-opacity-20'
            }`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <svg
              className="h-6 w-6 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span className="mt-1 text-center text-xs font-medium leading-tight">
              {t.nav.bookSession}
            </span>
          </LocalizedLink>

          {/* About */}
          <LocalizedLink
            to="/contact"
            className={`flex min-w-0 flex-1 flex-col items-center px-2 py-2 ${
              location.pathname.includes('/contact')
                ? 'text-blue-600'
                : 'text-gray-600 hover:text-blue-600'
            }`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <svg
              className="h-6 w-6 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="mt-1 break-words text-center text-xs leading-tight">
              {t.nav.about}
            </span>
          </LocalizedLink>

          {/* Authentication buttons or My Account - depends on auth state */}
          {isAuthenticated || isImpersonating ? (
            <LocalizedLink
              to="/account"
              className={`flex min-w-0 flex-1 flex-col items-center px-2 py-2 ${
                location.pathname.includes('/account')
                  ? 'text-blue-600'
                  : isImpersonating
                    ? 'text-orange-600 hover:text-orange-700'
                    : 'text-gray-600 hover:text-blue-600'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <svg
                className="h-6 w-6 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              <span className="mt-1 text-center text-xs leading-tight">{t.nav.myAccount}</span>
            </LocalizedLink>
          ) : (
            <>
              <button
                onClick={() =>
                  loginWithRedirect({
                    appState: {
                      returnTo: window.location.pathname,
                      from: 'mobile-nav',
                    },
                    authorizationParams: {
                      screen_hint: 'login',
                    },
                  })
                }
                className="flex min-w-0 flex-1 flex-col items-center px-2 py-2 text-gray-600 hover:text-blue-600"
              >
                <svg
                  className="h-6 w-6 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                  />
                </svg>
                <span className="mt-1 text-center text-xs leading-tight">{t.nav.login}</span>
              </button>
              <button
                onClick={() =>
                  loginWithRedirect({
                    appState: {
                      returnTo: window.location.pathname,
                      from: 'mobile-nav',
                    },
                    authorizationParams: {
                      screen_hint: 'signup',
                    },
                  })
                }
                className="flex min-w-0 flex-1 flex-col items-center px-2 py-2 text-gray-600 hover:text-blue-600"
              >
                <svg
                  className="h-6 w-6 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                  />
                </svg>
                <span className="mt-1 text-center text-xs leading-tight">{t.nav.signUp}</span>
              </button>
            </>
          )}

          {/* 5. Menu Toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`flex min-w-0 flex-1 flex-col items-center px-2 py-2 ${
              isMobileMenuOpen ? 'text-blue-600' : 'text-gray-600 hover:text-blue-600'
            }`}
          >
            {isMobileMenuOpen ? (
              <svg
                className="h-6 w-6 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg
                className="h-6 w-6 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
            <span className="mt-1 text-center text-xs leading-tight">Menu</span>
          </button>
        </div>
      </nav>

      <main className="flex-grow pb-20 md:pb-0">{children}</main>

      <Footer />
    </div>
  );
};

export default Layout;
